import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { 
  FiMic, FiMicOff, FiVideo, FiVideoOff, FiTv, FiPhoneOff, 
  FiMessageSquare, FiUsers, FiSend, FiDisc, FiClock, FiMove, FiX
} from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import { liveSessionAPI, userAPI } from "../../services/api";
import { Room, RoomEvent, VideoPresets, Track } from "livekit-client";

const T = {
  olive: "#6B8A5A", olive2: "#8BA47A",
  orange: "#F29A0C", orange2: "#E97818",
  dark: "#121214", mid: "#1E1E22", border: "rgba(255,255,255,0.08)",
  red: "#EF4444", green: "#22C55E", text: "#FFFFFF", textMuted: "#A1A1AA"
};

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:openrelay.metered.ca:80" },
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443?transport=tcp",
      username: "openrelayproject",
      credential: "openrelayproject"
    }
  ]
};

const optimizeSdpBandwidth = (sdp) => {
  let lines = sdp.split("\r\n");
  let videoIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].indexOf("m=video") === 0) {
      videoIndex = i;
      break;
    }
  }
  if (videoIndex !== -1) {
    lines.splice(videoIndex + 1, 0, "b=AS:2000");
  }
  return lines.join("\r\n");
};

const getInitials = (name) => {
  if (!name) return "?";
  const clean = name.replace(/\(.*\)/g, "").trim();
  const parts = clean.split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return clean.substring(0, 2).toUpperCase();
};

// Sub-component to safely render student streams in the grid
function StudentVideo({ stream, label, cameraState }) {
  const localRef = useRef(null);
  const [videoEnabled, setVideoEnabled] = useState(true);

  useEffect(() => {
    if (cameraState === false) {
      setVideoEnabled(false);
      return;
    }
    if (!stream) {
      setVideoEnabled(false);
      return;
    }

    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      setVideoEnabled(false);
      return;
    }

    // Set initial state
    setVideoEnabled(videoTrack.enabled && videoTrack.readyState === "live");

    const handleTrackState = () => {
      if (cameraState === false) {
        setVideoEnabled(false);
        return;
      }
      setVideoEnabled(videoTrack.enabled && videoTrack.readyState === "live");
    };

    // Periodically check in case mute/unmute events do not fire reliably
    const timer = setInterval(handleTrackState, 1500);

    videoTrack.addEventListener("mute", handleTrackState);
    videoTrack.addEventListener("unmute", handleTrackState);
    videoTrack.addEventListener("ended", handleTrackState);

    return () => {
      clearInterval(timer);
      videoTrack.removeEventListener("mute", handleTrackState);
      videoTrack.removeEventListener("unmute", handleTrackState);
      videoTrack.removeEventListener("ended", handleTrackState);
    };
  }, [stream, cameraState]);

  const handleRef = (el) => {
    localRef.current = el;
    if (el && stream) {
      if (el.srcObject !== stream) {
        el.srcObject = stream;
      }
      if (el.paused) {
        el.play().catch(err => console.warn("Autoplay blocked for student stream", err));
      }
    }
  };

  return (
    <div 
      onClick={() => {
        if (localRef.current && videoEnabled) {
          console.log("[WebRTC] Manually triggering play on student feed.");
          localRef.current.play().catch(err => console.error("Manual student feed play failed", err));
        }
      }}
      className="rounded-xl overflow-hidden bg-zinc-900 border border-white/5 aspect-video relative cursor-pointer flex items-center justify-center"
    >
      {videoEnabled ? (
        <video 
          ref={handleRef}
          autoPlay 
          playsInline
          className="w-full h-full object-cover scale-x-[-1]"
        />
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-full bg-[#1A1A1E]">
          <div className="w-12 h-12 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/35 flex items-center justify-center text-white font-black text-sm tracking-wider">
            {getInitials(label)}
          </div>
        </div>
      )}
      <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded bg-black/75 text-[9px] font-bold text-white/90">
        {label}
      </div>
    </div>
  );
}

export default function LiveRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClassEndedModal, setShowClassEndedModal] = useState(false);
  
  const [connectionStatus, setConnectionStatus] = useState("Initializing media...");

  // Roles & Connection IDs
  const rawRole = localStorage.getItem("divyayoga_role") || "user";
  const role = (rawRole.toLowerCase() === "admin" || rawRole.toLowerCase() === "superadmin") ? "admin" : "user";
  const [myId, setMyId] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      let fullName = "Participant";
      try {
        const res = await userAPI.getProfile();
        if (res.data?.success && res.data.data?.fullName) {
          fullName = res.data.data.fullName;
        }
      } catch (err) {
        console.warn("Failed to fetch user profile, using localStorage fallback", err);
        const userObj = localStorage.getItem("divyayoga_user") ? JSON.parse(localStorage.getItem("divyayoga_user")) : null;
        if (userObj?.fullName) fullName = userObj.fullName;
      }
      const safeName = fullName.replace(/_/g, " ");
      const rand = Math.random().toString(36).substring(2, 6);
      setMyId(`peer_${safeName}_${rand}`);
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    if (showClassEndedModal) {
      const timer = setTimeout(() => {
        navigate("/user/livesession");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showClassEndedModal, navigate]);
  
  // Responsive check: screens < 1024px are mobile/tablet
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1024;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Media Controls
  const [micActive, setMicActive] = useState(true);
  const [camActive, setCamActive] = useState(true);
  const [sharing, setSharing] = useState(false);
  // Default open only on desktop (>=1024px); on mobile keep closed so live video section is visible immediately
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1024;
    }
    return false;
  });
  const [sidebarTab, setSidebarTab] = useState("chat");

  const toggleChat = () => {
    if (sidebarOpen && sidebarTab === "chat") {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
      setSidebarTab("chat");
    }
  };

  const toggleParticipants = () => {
    if (sidebarOpen && sidebarTab === "participants") {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
      setSidebarTab("participants");
    }
  };
  
  // Streams
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({}); // studentId -> MediaStream
  const [instructorStream, setInstructorStream] = useState(null);
  const [instructorVideoEnabled, setInstructorVideoEnabled] = useState(true);

  useEffect(() => {
    if (!instructorStream) {
      setInstructorVideoEnabled(false);
      return;
    }

    const videoTrack = instructorStream.getVideoTracks()[0];
    if (!videoTrack) {
      setInstructorVideoEnabled(false);
      return;
    }

    setInstructorVideoEnabled(videoTrack.enabled && videoTrack.readyState === "live");

    const handleTrackState = () => {
      setInstructorVideoEnabled(videoTrack.enabled && videoTrack.readyState === "live");
    };

    const timer = setInterval(handleTrackState, 1500);

    videoTrack.addEventListener("mute", handleTrackState);
    videoTrack.addEventListener("unmute", handleTrackState);
    videoTrack.addEventListener("ended", handleTrackState);

    return () => {
      clearInterval(timer);
      videoTrack.removeEventListener("mute", handleTrackState);
      videoTrack.removeEventListener("unmute", handleTrackState);
      videoTrack.removeEventListener("ended", handleTrackState);
    };
  }, [instructorStream]);
  
  // Connection states & buffers
  const roomRef = useRef(null);
  const peerConnections = useRef({}); // peerId -> RTCPeerConnection
  const pendingCandidates = useRef({}); // peerId -> Array of candidate signals
  const localStreamRef = useRef(null);
  const joinTimeRef = useRef(Date.now());
  
  const localVideoRef = useRef(null);
  const remoteSpotlightRef = useRef(null);
  const stageRef = useRef(null);

  const [cameraStates, setCameraStates] = useState({}); // peerId -> boolean (enabled)

  // Chat
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  // Media Recording for Class Replay
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);

  const startRecording = (stream) => {
    if (!stream) return;
    recordedChunksRef.current = [];
    try {
      let options = { mimeType: "video/webm;codecs=vp9,opus" };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = "video/webm;codecs=vp8,opus";
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = "video/mp4";
      }
      
      const recorder = new MediaRecorder(stream, options);
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      
      recorder.start(1000); // chunk every 1s
      mediaRecorderRef.current = recorder;
      console.log("MediaRecorder started successfully on local stream.");
    } catch (e) {
      console.error("Failed to start MediaRecorder:", e);
    }
  };

  // Bind instructor stream to spotlight video element with callback ref
  const handleRemoteSpotlightRef = React.useCallback((el) => {
    remoteSpotlightRef.current = el;
    if (el && instructorStream) {
      if (el.srcObject !== instructorStream) {
        el.srcObject = instructorStream;
      }
      if (el.paused) {
        el.play().catch(err => console.warn("Autoplay blocked for instructor stream", err));
      }
    }
  }, [instructorStream]);

  // Bind local stream to preview video element with callback ref
  const handleLocalVideoRef = React.useCallback((el) => {
    localVideoRef.current = el;
    if (el && localStream) {
      if (el.srcObject !== localStream) {
        el.srcObject = localStream;
      }
      if (el.paused) {
        el.play().catch(err => console.warn("Autoplay blocked for local stream", err));
      }
    }
  }, [localStream]);

  const [showCapacityFullModal, setShowCapacityFullModal] = useState(false);
  const [showEarlyLockModal, setShowEarlyLockModal] = useState(false);
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(true);

  // 1. Fetch Session details & enforce capacity & early launch limits
  useEffect(() => {
    const loadSession = async () => {
      try {
        const res = await liveSessionAPI.getSession(id);
        if (res.data?.success) {
          const s = res.data.data;
          setSession(s);

          // Early launch restriction: studio unlocks 2 minutes before scheduled class
          if (s.scheduledAt) {
            const schedTimeStr = s.scheduledAt;
            const start = new Date(schedTimeStr.endsWith("Z") || schedTimeStr.includes("+") || schedTimeStr.includes("-") ? schedTimeStr : schedTimeStr + "Z");
            const duration = s.durationInMinutes || s.duration || 45;
            const end = new Date(start.getTime() + duration * 60000);
            const now = new Date();
            const twoMinutesBefore = new Date(start.getTime() - 2 * 60000);

            if (now < twoMinutesBefore) {
              setShowEarlyLockModal(true);
              setLoading(false);
              return;
            }
          }

          // Capacity limit enforcement for non-admin participants
          const maxCap = s.maxCapacity || s.capacity || 10;
          const currentOccupancy = s.registered || (s.rosterDetails ? s.rosterDetails.length : 0);
          if (role !== "admin" && currentOccupancy >= maxCap) {
            setShowCapacityFullModal(true);
            setLoading(false);
            return;
          }
        }
        // Auto-log attendance & activity in DB if student joins via direct link
        if (role !== "admin") {
          try {
            const joinISO = new Date().toISOString();
            await liveSessionAPI.checkIn(id, { joinedAt: joinISO, watchMins: 1 });
            toast.success("✓ Welcome to live class! Attendance logged automatically.", { autoClose: 3000, toastId: `auto-checkin-${id}` });
          } catch (e) {
            console.warn("Direct link attendance auto-log:", e);
          }
        }
      } catch (err) {
        console.error("Failed to load live room details", err);
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [id, role]);

  // Real-time Live Room Duration Sync to backend
  useEffect(() => {
    if (role === "admin" || showCapacityFullModal || showEarlyLockModal) return;
    const interval = setInterval(async () => {
      const elapsedMins = Math.max(1, Math.round((Date.now() - joinTimeRef.current) / 60000));
      try {
        await liveSessionAPI.checkIn(id, { watchMins: elapsedMins });
      } catch (err) {
        // silent retry
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      const finalMins = Math.max(1, Math.round((Date.now() - joinTimeRef.current) / 60000));
      liveSessionAPI.checkIn(id, { watchMins: finalMins }).catch(() => {});
    };
  }, [id, role, showCapacityFullModal, showEarlyLockModal]);

  // 2. Connect to LiveKit SFU Cloud (Supports 50+ to 100+ participants with ultra-low latency)
  useEffect(() => {
    if (!id || showEarlyLockModal || showCapacityFullModal) return;
    let isCleanedUp = false;

    const connectLiveKit = async () => {
      try {
        setConnectionStatus("Acquiring LiveKit room token...");
        const tokenRes = await liveSessionAPI.getLiveKitToken(id);
        if (!tokenRes.data?.success || !tokenRes.data.token) {
          toast.error("Could not obtain session token.");
          setConnectionStatus("Token acquisition failed.");
          return;
        }

        const { token, serverUrl } = tokenRes.data;

        if (isCleanedUp) return;

        setConnectionStatus("Connecting to LiveKit SFU Cloud...");
        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          videoCaptureDefaults: {
            resolution: VideoPresets.h720.resolution
          }
        });
        roomRef.current = room;

        // Remote track subscribed
        room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          console.log(`[LiveKit] Subscribed to track ${track.kind} from ${participant.identity}`);
          const isInst = participant.identity.startsWith("instructor_") || role === "user";

          if (track.kind === Track.Kind.Video) {
            const mStream = track.mediaStream || new MediaStream([track.mediaStreamTrack]);
            if (isInst) {
              setInstructorStream(mStream);
              setInstructorVideoEnabled(true);
              if (remoteSpotlightRef.current) {
                track.attach(remoteSpotlightRef.current);
              }
            } else {
              setRemoteStreams(prev => ({
                ...prev,
                [participant.identity]: mStream
              }));
              setCameraStates(prev => ({ ...prev, [participant.identity]: !track.isMuted }));
            }
          } else if (track.kind === Track.Kind.Audio) {
            const audioEl = track.attach();
            document.body.appendChild(audioEl);
          }
        });

        // Remote track unsubscribed
        room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
          track.detach();
          const isInst = participant.identity.startsWith("instructor_") || role === "user";
          if (isInst) {
            setInstructorStream(null);
          } else {
            setRemoteStreams(prev => {
              const copy = { ...prev };
              delete copy[participant.identity];
              return copy;
            });
          }
        });

        // Track mute / unmute
        room.on(RoomEvent.TrackMuted, (publication, participant) => {
          if (publication.kind === Track.Kind.Video) {
            const isInst = participant.identity.startsWith("instructor_") || role === "user";
            if (isInst) {
              setInstructorVideoEnabled(false);
            } else {
              setCameraStates(prev => ({ ...prev, [participant.identity]: false }));
            }
          }
        });

        room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
          if (publication.kind === Track.Kind.Video) {
            const isInst = participant.identity.startsWith("instructor_") || role === "user";
            if (isInst) {
              setInstructorVideoEnabled(true);
            } else {
              setCameraStates(prev => ({ ...prev, [participant.identity]: true }));
            }
          }
        });

        // Real-time Chat & Signaling Data Packets
        room.on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const str = new TextDecoder().decode(payload);
            const data = JSON.parse(str);
            if (data.type === "class-ended") {
              setShowClassEndedModal(true);
              if (roomRef.current) roomRef.current.disconnect();
              return;
            }
            setMessages(prev => [...prev, data]);
          } catch (e) {
            console.warn("LiveKit data received parse error:", e);
          }
        });

        // Disconnected
        room.on(RoomEvent.Disconnected, () => {
          console.log("[LiveKit] Room disconnected.");
          setConnectionStatus("Disconnected from room.");
        });

        await room.connect(serverUrl, token);
        setConnectionStatus("Connected (LiveKit SFU Active)");

        // Publish local camera & mic
        try {
          await room.localParticipant.setCameraEnabled(camActive);
          await room.localParticipant.setMicrophoneEnabled(micActive);

          const localVideoPub = Array.from(room.localParticipant.videoTrackPublications.values())[0];
          if (localVideoPub && localVideoPub.track) {
            const lStream = localVideoPub.track.mediaStream || new MediaStream([localVideoPub.track.mediaStreamTrack]);
            setLocalStream(lStream);
            localStreamRef.current = lStream;
            if (localVideoRef.current) {
              localVideoPub.track.attach(localVideoRef.current);
            }
            if (role === "admin") {
              startRecording(lStream);
            }
          }
        } catch (mediaErr) {
          console.warn("[LiveKit] Media publish warning:", mediaErr);
        }
      } catch (err) {
        if (!isCleanedUp) {
          console.error("[LiveKit] Connection failed:", err);
          setConnectionStatus("Connection failed. Retrying...");
        }
      }
    };

    connectLiveKit();

    return () => {
      isCleanedUp = true;
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, [id, showEarlyLockModal, showCapacityFullModal]);

  // Chat message send handler via LiveKit Data Channel
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    let senderName = "Participant";
    if (role === "admin") {
      // Prioritize the class instructor's name (e.g. "Divya") instead of "Super Admin"
      const instName = session?.instructorName?.trim();
      const rawUser = localStorage.getItem("divyayoga_user") ? JSON.parse(localStorage.getItem("divyayoga_user")) : null;
      const userFullName = rawUser?.fullName?.trim();

      const chosenName = instName || (userFullName && !userFullName.toLowerCase().includes("super admin") ? userFullName : "Instructor");
      senderName = `${chosenName} (Instructor)`;
    } else {
      const rawUser = localStorage.getItem("divyayoga_user") ? JSON.parse(localStorage.getItem("divyayoga_user")) : null;
      senderName = rawUser?.fullName || "Student Participant";
    }
    
    const msg = {
      id: Date.now(),
      sender: senderName,
      text: chatInput.trim(),
      time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
      isInstructor: role === "admin"
    };

    setMessages(prev => [...prev, msg]);
    setChatInput("");

    if (roomRef.current) {
      try {
        const payload = new TextEncoder().encode(JSON.stringify(msg));
        await roomRef.current.localParticipant.publishData(payload, { reliable: true });
      } catch (err) {
        console.warn("Failed to publish chat message via LiveKit:", err);
      }
    }
  };

  // Mute / Unmute Audio track
  const toggleMic = async () => {
    const nextState = !micActive;
    setMicActive(nextState);
    if (roomRef.current) {
      await roomRef.current.localParticipant.setMicrophoneEnabled(nextState).catch(console.warn);
    }
  };

  // Toggle Video track
  const toggleCam = async () => {
    const nextState = !camActive;
    setCamActive(nextState);
    if (roomRef.current) {
      await roomRef.current.localParticipant.setCameraEnabled(nextState).catch(console.warn);
      if (nextState && localVideoRef.current) {
        const pub = Array.from(roomRef.current.localParticipant.videoTrackPublications.values())[0];
        if (pub && pub.track) {
          pub.track.attach(localVideoRef.current);
        }
      }
    }
  };

  const toggleShare = async () => {
    const nextState = !sharing;
    setSharing(nextState);
    if (roomRef.current) {
      await roomRef.current.localParticipant.setScreenShareEnabled(nextState).catch(console.warn);
    }
  };

  const handleLeave = async () => {
    if (role === "admin") {
      // Notify all connected students that the class has ended
      if (roomRef.current) {
        try {
          const endMsg = new TextEncoder().encode(JSON.stringify({ type: "class-ended" }));
          await roomRef.current.localParticipant.publishData(endMsg, { reliable: true });
        } catch (err) {
          console.error("Failed to broadcast class-ended signal", err);
        }
      }

      setIsUploadingRecording(true);
      setConnectionStatus("Saving session recording to cloud... Please wait.");
      
      const finishExit = () => {
        if (roomRef.current) {
          roomRef.current.disconnect();
        }
        navigate("/admin/livesession");
      };

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.onstop = async () => {
          const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
          if (blob.size > 1000) {
            try {
              const file = new File([blob], `livesession_recording_${id}.webm`, { type: "video/webm" });
              const formData = new FormData();
              formData.append("file", file);
              
              const uploadRes = await liveSessionAPI.uploadVideo(formData);
              const videoUrl = uploadRes.data?.fileUrl || uploadRes.data?.url;
              if (videoUrl) {
                await liveSessionAPI.setRecordingUrl(id, { videoUrl });
                console.log("Recorded class replay successfully linked:", videoUrl);
              }
            } catch (err) {
              console.error("Failed to upload recording to R2:", err);
            }
          }
          finishExit();
        };
        
        mediaRecorderRef.current.stop();
      } else {
        finishExit();
      }
    } else {
      // Students leave: calculate exact active time spent in live room
      const elapsedMins = Math.max(1, Math.round((Date.now() - joinTimeRef.current) / 60000));
      try {
        await userAPI.logMindfulness({
          minutes: elapsedMins,
          source: "Live Session",
          activityName: session?.title || "Live Session"
        });
      } catch (e) {
        console.warn("Failed to log active live session time:", e);
      }

      if (roomRef.current) {
        roomRef.current.disconnect();
      }
      navigate("/user/livesession");
    }
  };

  const title = session?.title || "Live Yoga Interactive Session";
  const instructor = session?.instructorName || "Instructor TBA";

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0F0F11] text-white overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      
      {/* ── HEADER ── */}
      <div className="h-16 px-3 sm:px-6 border-b flex items-center justify-between z-10" style={{ background: "rgba(20,20,24,0.7)", borderColor: T.border, backdropFilter: "blur(12px)" }}>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xs sm:text-sm font-bold truncate max-w-[150px] sm:max-w-md tracking-tight">{title}</h1>
            <p className="text-[10px] sm:text-[11px] text-zinc-400 truncate">{instructor} · {session?.durationInMinutes || 60}m</p>
          </div>
        </div>
        
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {/* Diagnostic status text */}
          <div className="hidden sm:block text-[10px] font-bold px-3 py-1 rounded bg-zinc-800 text-zinc-400 border border-white/5 uppercase tracking-wider">
            {connectionStatus}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20">
            <FiDisc className="text-red-500 animate-pulse" size={12} />
            <span className="text-[10px] font-black uppercase text-red-500 tracking-wider">LIVE</span>
          </div>
          {role === "admin" ? (
            <div className="flex items-center gap-1.5">
              <button onClick={toggleParticipants} className={`p-2 rounded-lg transition ${sidebarOpen && sidebarTab === "participants" ? "bg-[#F29A0C] text-white" : "bg-zinc-800/50 hover:bg-zinc-800 text-zinc-300"}`} title="Toggle Participants">
                <FiUsers size={16} />
              </button>
              <button onClick={toggleChat} className={`p-2 rounded-lg transition ${sidebarOpen && sidebarTab === "chat" ? "bg-[#F29A0C] text-white" : "bg-zinc-800/50 hover:bg-zinc-800 text-zinc-300"}`} title="Toggle Chat">
                <FiMessageSquare size={16} />
              </button>
            </div>
          ) : (
            <button onClick={toggleChat} className={`p-2 rounded-lg transition ${sidebarOpen && sidebarTab === "chat" ? "bg-[#F29A0C] text-white" : "bg-zinc-800/50 hover:bg-zinc-800 text-zinc-300"}`} title="Toggle Chat">
              <FiMessageSquare size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Privacy & Recording Notice Banner */}
      {showPrivacyBanner && (
        <div className="bg-amber-500/10 border-y border-amber-500/20 px-6 py-2.5 flex items-center justify-between gap-3 text-amber-200 text-xs z-20">
          <div className="flex items-center gap-2">
            <span className="text-base">📹</span>
            <span>
              <strong>Live Recording & Privacy Notice:</strong> This class is hosted live by Divyyog instructors. 
              Only the instructor's screen and broadcast are recorded for class replays. No user or student screen is recorded.
            </span>
          </div>
          <button 
            onClick={() => setShowPrivacyBanner(false)}
            className="text-[11px] font-bold underline hover:text-white shrink-0 cursor-pointer bg-transparent border-none"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── MAIN WORKSPACE ── */}
      <div className="flex-1 flex overflow-hidden relative">
        
        {/* Left: Video Area */}
        <div ref={stageRef} className="flex-1 p-3 sm:p-4 lg:p-6 flex flex-col justify-between relative bg-[#09090A] overflow-hidden">
          
          <div 
            onClick={() => {
              if (remoteSpotlightRef.current) {
                console.log("[WebRTC] Manually triggering play on remote spotlight.");
                remoteSpotlightRef.current.play().catch(e => console.error("Manual spotlight play failed", e));
              }
            }}
            className="flex-1 rounded-[28px] overflow-hidden relative border border-white/5 bg-zinc-900/60 shadow-2xl flex items-center justify-center cursor-pointer"
          >
            
            {/* SPOTLIGHT VIEW */}
            {role === "admin" ? (
              localStream ? (
                camActive ? (
                  <video 
                    ref={handleLocalVideoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-full object-cover scale-x-[-1]" 
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center w-full h-full bg-[#1A1A1E]">
                    <div className="w-24 h-24 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/35 flex items-center justify-center text-white font-black text-2xl tracking-wider shadow-xl">
                      {getInitials(instructor)}
                    </div>
                  </div>
                )
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-zinc-950">
                  <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4 animate-pulse">
                    <span className="text-3xl">📷</span>
                  </div>
                  <h3 className="font-bold text-lg mb-1 text-red-400">Camera Unavailable</h3>
                  <p className="text-xs text-zinc-400 max-w-sm leading-relaxed">
                    Your camera is currently blocked or in use by another browser (e.g. Edge). 
                    <br />
                    <span className="text-[10px] text-zinc-500 mt-1 block">Please close other tabs using the camera or test on separate devices.</span>
                  </p>
                </div>
              )
            ) : (
              instructorStream ? (
                (instructorVideoEnabled && cameraStates["instructor"] !== false) ? (
                  <video 
                    ref={handleRemoteSpotlightRef} 
                    autoPlay 
                    playsInline 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center w-full h-full bg-[#1A1A1E]">
                    <div className="w-24 h-24 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/35 flex items-center justify-center text-white font-black text-2xl tracking-wider shadow-xl">
                      {getInitials(instructor)}
                    </div>
                  </div>
                )
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-zinc-950">
                  <div className="w-20 h-20 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/30 flex items-center justify-center mb-4">
                    <span className="text-3xl animate-bounce">🧘‍♀️</span>
                  </div>
                  <h3 className="font-bold text-lg mb-1">{instructor} (Instructor)</h3>
                  <p className="text-xs text-zinc-400 max-w-sm">Waiting for the instructor to connect their stream...</p>
                </div>
              )
            )}

            {/* Float Tag */}
            <div className="absolute top-4 left-4 px-3 py-1 rounded-lg bg-black/60 backdrop-blur-md text-[11px] font-bold border border-white/10">
              {role === "admin" ? "Instructor Spotlight (You)" : `${instructor} (Instructor Feed)`}
            </div>
          </div>

          {/* Student local floating card (Draggable Zoom/Meet PiP) */}
          {role !== "admin" && (
            <motion.div 
              drag
              dragConstraints={stageRef}
              dragElastic={0.06}
              dragMomentum={false}
              whileHover={{ scale: 1.02 }}
              whileDrag={{ scale: 1.04, cursor: "grabbing" }}
              className="absolute bottom-24 sm:bottom-28 right-4 sm:right-8 w-36 sm:w-56 aspect-video rounded-[18px] sm:rounded-[20px] overflow-hidden border-2 border-white/20 bg-zinc-950/90 shadow-2xl z-20 flex items-center justify-center cursor-grab active:cursor-grabbing backdrop-blur-md group select-none transition-shadow hover:shadow-[#F29A0C]/25 hover:border-[#F29A0C]/50"
            >
              {/* Drag Handle & Mic Status Header */}
              <div className="absolute top-2 left-2 right-2 flex items-center justify-between pointer-events-none z-10 opacity-75 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-1.5 bg-black/70 backdrop-blur-md px-2 py-0.5 rounded-full text-[9px] font-bold text-white/90 border border-white/10">
                  <FiMove size={10} className="text-[#F29A0C]" />
                  <span>Drag</span>
                </div>
                <div className="flex items-center gap-1 bg-black/70 backdrop-blur-md px-1.5 py-0.5 rounded-full border border-white/10">
                  {micActive ? (
                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_#10B981]" title="Mic On" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-red-500" title="Mic Muted" />
                  )}
                </div>
              </div>

              {localStream ? (
                camActive ? (
                  <>
                    <video 
                      ref={handleLocalVideoRef} 
                      autoPlay 
                      playsInline 
                      muted 
                      className="w-full h-full object-cover scale-x-[-1] pointer-events-none" 
                    />
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/80 backdrop-blur-sm text-[9px] font-bold text-white/90 border border-white/10 pointer-events-none">
                      You (Student)
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col items-center justify-center w-full h-full bg-[#1A1A1E] pointer-events-none">
                      <div className="w-10 h-10 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/35 flex items-center justify-center text-white font-black text-xs tracking-wider mb-1">
                        {getInitials(myId ? myId.split("_")[1] : "Student")}
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/80 backdrop-blur-sm text-[9px] font-bold text-white/90 border border-white/10 pointer-events-none">
                      You (Student)
                    </div>
                  </>
                )
              ) : (
                <div className="text-[9px] text-zinc-400 text-center p-2 font-bold bg-zinc-950/80 w-full h-full flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-zinc-500 mb-0.5">📷</span>
                  <span>Camera Unavailable</span>
                </div>
              )}
            </motion.div>
          )}

          {/* Controls Bar Overlay */}
          <div className="h-16 sm:h-20 flex items-center justify-center gap-2 sm:gap-4 mt-2 sm:mt-6 flex-wrap">
            
            {/* Mic Toggle */}
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={toggleMic}
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer border-none shadow-lg transition-colors ${micActive ? "bg-zinc-800 hover:bg-zinc-700 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
            >
              {micActive ? <FiMic className="text-base sm:text-lg" /> : <FiMicOff className="text-base sm:text-lg" />}
            </motion.button>

            {/* Camera Toggle */}
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={toggleCam}
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer border-none shadow-lg transition-colors ${camActive ? "bg-zinc-800 hover:bg-zinc-700 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
            >
              {camActive ? <FiVideo className="text-base sm:text-lg" /> : <FiVideoOff className="text-base sm:text-lg" />}
            </motion.button>

            {/* Screen Share Toggle */}
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={toggleShare}
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer border-none shadow-lg transition-colors ${sharing ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"}`}
            >
              <FiTv className="text-base sm:text-lg" />
            </motion.button>

            {/* Chat Toggle (All Roles) */}
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={toggleChat}
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer border-none shadow-lg transition-colors ${sidebarOpen && sidebarTab === "chat" ? "bg-[#F29A0C] text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"}`}
              title="Toggle Class Chat"
            >
              <FiMessageSquare className="text-base sm:text-lg" />
            </motion.button>

            {/* Participants List Toggle (Instructor Only) */}
            {role === "admin" && (
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                onClick={toggleParticipants}
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer border-none shadow-lg transition-colors ${sidebarOpen && sidebarTab === "participants" ? "bg-[#F29A0C] text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"}`}
                title="Toggle Participant List"
              >
                <FiUsers className="text-base sm:text-lg" />
              </motion.button>
            )}

            {/* End Call / Leave Room */}
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              onClick={handleLeave}
              className="px-4 sm:px-6 h-10 sm:h-12 rounded-full flex items-center gap-1.5 sm:gap-2 bg-red-600 hover:bg-red-700 text-white border-none cursor-pointer font-bold text-[11px] sm:text-xs uppercase tracking-wider shadow-lg"
            >
              <FiPhoneOff size={14} />
              {role === "admin" ? "End Class" : "Leave Room"}
            </motion.button>
          </div>
        </div>

        {/* Mobile Backdrop when sidebar drawer is open */}
        {isMobile && sidebarOpen && (
          <div 
            onClick={() => setSidebarOpen(false)} 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 transition-opacity"
          />
        )}

        {/* Right: Sidebar Panel (Chat & Student Feeds Grid) */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div 
              initial={isMobile ? { x: "100%", opacity: 0 } : { width: 0, opacity: 0 }} 
              animate={isMobile ? { x: 0, opacity: 1 } : { width: 340, opacity: 1 }} 
              exit={isMobile ? { x: "100%", opacity: 0 } : { width: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`border-l flex flex-col h-full bg-[#131316] ${
                isMobile 
                  ? "fixed inset-y-0 right-0 z-40 w-full sm:w-[380px] shadow-2xl" 
                  : "relative flex-shrink-0"
              }`}
              style={{ borderColor: T.border }}
            >
              {role === "admin" ? (
                // Instructor layout: Single active panel tab based on sidebarTab
                <div className="flex flex-col h-full overflow-hidden">
                  {sidebarTab === "participants" ? (
                    /* Student Feeds / Participant List taking full height */
                    <div className="flex flex-col h-full overflow-hidden">
                      <div className="px-4 py-4 border-b flex items-center justify-between bg-zinc-900/40" style={{ borderColor: T.border }}>
                        <span className="text-[10px] font-black uppercase text-[#F29A0C] tracking-wider flex items-center gap-1.5 font-bold">
                          <FiUsers size={14} /> Student Participants ({Object.keys(remoteStreams).length})
                        </span>
                        <button 
                          onClick={() => setSidebarOpen(false)}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer border-none bg-transparent"
                          title="Close panel"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 scrollbar-thin">
                        {Object.entries(remoteStreams).map(([peerId, stream]) => (
                          <StudentVideo 
                            key={peerId} 
                            stream={stream} 
                            label={peerId.split("_")[1] || "Student"} 
                            cameraState={cameraStates[peerId]}
                          />
                        ))}
                        {Object.keys(remoteStreams).length === 0 && (
                          <div className="text-center py-12 text-zinc-500 text-[11.5px] font-semibold leading-relaxed">
                            No students have joined or connected camera feeds yet.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Class Chat taking full height */
                    <div className="flex flex-col h-full overflow-hidden">
                      <div className="px-4 py-4 bg-zinc-900/40 border-b flex items-center justify-between" style={{ borderColor: T.border }}>
                        <span className="text-[10px] font-black uppercase text-[#F29A0C] tracking-wider flex items-center gap-1.5 font-bold">
                          <FiMessageSquare size={14} /> Class Chat
                        </span>
                        <button 
                          onClick={() => setSidebarOpen(false)}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer border-none bg-transparent"
                          title="Close chat"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 scrollbar-thin">
                        {messages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full text-center py-16 px-4 text-zinc-500">
                            <FiMessageSquare size={24} className="mb-2 opacity-40 text-[#F29A0C]" />
                            <p className="text-xs font-semibold text-zinc-400">No messages yet</p>
                            <p className="text-[10px] text-zinc-500 mt-0.5">Send a message to start the class chat!</p>
                          </div>
                        ) : (
                          messages.map((m) => (
                            <div key={m.id} className="flex flex-col gap-1 max-w-[90%]">
                              <div className="flex items-baseline gap-2">
                                <span className={`text-[11px] font-bold ${m.isInstructor ? "text-[#F29A0C]" : "text-zinc-400"}`}>
                                  {m.isInstructor && m.sender?.toLowerCase().includes("super admin") && session?.instructorName
                                    ? `${session.instructorName} (Instructor)`
                                    : m.sender}
                                </span>
                                <span className="text-[9px] text-zinc-600">{m.time}</span>
                              </div>
                              <div className={`p-3 rounded-[16px] text-xs leading-relaxed ${m.isInstructor ? "bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-white" : "bg-zinc-800/40 text-zinc-300"}`}>
                                {m.text}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      {/* Chat Input */}
                      <div className="p-4 border-t" style={{ borderColor: T.border }}>
                        <div className="flex items-center gap-2 bg-zinc-900/60 rounded-xl p-1.5 border border-white/5">
                          <input 
                            value={chatInput} 
                            onChange={e => setChatInput(e.target.value)} 
                            onKeyDown={e => e.key === "Enter" && handleSendMessage()}
                            placeholder="Say something to the class..." 
                            className="flex-1 bg-transparent border-none outline-none text-xs text-white p-2"
                          />
                          <button onClick={handleSendMessage} className="w-8 h-8 rounded-lg bg-[#F29A0C] hover:bg-[#E97818] text-white border-none cursor-pointer flex items-center justify-center transition-colors">
                            <FiSend size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Student layout: Full-height Chat (No supervising feeds)
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="px-4 py-4 border-b flex items-center justify-between bg-zinc-900/40" style={{ borderColor: T.border }}>
                    <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider flex items-center gap-1.5">
                      <FiMessageSquare size={12} className="text-[#F29A0C]" /> Live Class Chat
                    </span>
                    <button 
                      onClick={() => setSidebarOpen(false)}
                      className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer border-none bg-transparent"
                      title="Close chat"
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 scrollbar-thin">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-16 px-4 text-zinc-500">
                        <FiMessageSquare size={24} className="mb-2 opacity-40 text-[#F29A0C]" />
                        <p className="text-xs font-semibold text-zinc-400">No messages yet</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">Say hello to the class to get started!</p>
                      </div>
                    ) : (
                      messages.map((m) => (
                        <div key={m.id} className="flex flex-col gap-1 max-w-[90%]">
                          <div className="flex items-baseline gap-2">
                            <span className={`text-[11px] font-bold ${m.isInstructor ? "text-[#F29A0C]" : "text-zinc-400"}`}>
                              {m.isInstructor && m.sender?.toLowerCase().includes("super admin") && session?.instructorName
                                ? `${session.instructorName} (Instructor)`
                                : m.sender}
                            </span>
                            <span className="text-[9px] text-zinc-600">{m.time}</span>
                          </div>
                          <div className={`p-3 rounded-[16px] text-xs leading-relaxed ${m.isInstructor ? "bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-white" : "bg-zinc-800/40 text-zinc-300"}`}>
                            {m.text}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Chat Input */}
                  <div className="p-4 border-t" style={{ borderColor: T.border }}>
                    <div className="flex items-center gap-2 bg-zinc-900/60 rounded-xl p-1.5 border border-white/5">
                      <input 
                        value={chatInput} 
                        onChange={e => setChatInput(e.target.value)} 
                        onKeyDown={e => e.key === "Enter" && handleSendMessage()}
                        placeholder="Say something to the class..." 
                        className="flex-1 bg-transparent border-none outline-none text-xs text-white p-2"
                      />
                      <button onClick={handleSendMessage} className="w-8 h-8 rounded-lg bg-[#F29A0C] hover:bg-[#E97818] text-white border-none cursor-pointer flex items-center justify-center transition-colors">
                        <FiSend size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {isUploadingRecording && (
        <div className="fixed inset-0 bg-black/90 backdrop-filter backdrop-blur-md z-50 flex flex-col items-center justify-center">
          <GiLotus className="text-[72px] text-[#F29A0C] animate-spin-slow mb-4" />
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Processing Class Recording</h2>
          <p className="text-sm text-zinc-400 max-w-xs text-center leading-relaxed">
            Uploading the recorded interactive session. Please do not close your browser.
          </p>
        </div>
      )}

      {/* Class Ended Popup Overlay */}
      <AnimatePresence>
        {showClassEndedModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-lg z-[2000] flex items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} 
              animate={{ scale: 1, y: 0 }} 
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              className="w-full max-w-sm rounded-[32px] p-8 text-center"
              style={{ background: "rgba(25,25,29,0.95)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}
            >
              <div className="w-16 h-16 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 flex items-center justify-center mx-auto mb-6">
                <GiLotus size={32} className="text-[#F29A0C]" />
              </div>
              <h2 className="text-xl font-black mb-3 tracking-tight text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Class Ended
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                The instructor has ended this live class session. Thank you for practicing with us today!
              </p>
              <div className="text-[11px] text-zinc-500 font-bold uppercase tracking-wider mb-6 flex items-center justify-center gap-1 bg-white/5 py-2.5 rounded-xl border border-white/5">
                Redirecting in <span className="text-white">4 seconds...</span>
              </div>
              <button 
                onClick={() => navigate("/user/livesession")} 
                className="w-full h-12 rounded-full font-bold text-xs uppercase tracking-wider bg-white hover:bg-zinc-100 text-black border-none cursor-pointer shadow-lg transition-all active:scale-95"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Return to Dashboard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Class Capacity Limit Full Modal Popup */}
      <AnimatePresence>
        {showCapacityFullModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2500] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-red-500/30 rounded-3xl p-7 max-w-md w-full text-center shadow-2xl relative overflow-hidden"
            >
              <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4 text-red-500">
                <FiUsers size={28} />
              </div>
              <h3 className="text-xl font-extrabold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Class Capacity Limit Reached!
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed mb-6">
                This live yoga session has reached its maximum capacity limit of{" "}
                <strong className="text-amber-400">{session?.maxCapacity || session?.capacity || 10} participants</strong> set by the instructor.
                No more students can join at this time.
              </p>
              <button
                onClick={() => navigate("/user/livesession")}
                className="w-full py-3 rounded-full text-xs font-black uppercase tracking-wider text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 shadow-lg cursor-pointer border-none transition-all active:scale-95"
              >
                Return to Live Sessions
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Early Launch / Early Access Lock Modal */}
      <AnimatePresence>
        {showEarlyLockModal && session && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2500] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-amber-500/30 rounded-3xl p-7 max-w-md w-full text-center shadow-2xl relative overflow-hidden"
            >
              <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4 text-amber-500">
                <FiClock size={28} className="animate-pulse" />
              </div>
              <h3 className="text-xl font-extrabold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Class Not Live Yet
              </h3>
              <p className="text-xs text-zinc-300 leading-relaxed mb-4">
                <strong>{session.title}</strong> is scheduled for{" "}
                <span className="text-amber-400 font-bold">
                  {new Date(session.scheduledAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true })}
                </span>.
              </p>
              <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[11.5px] leading-relaxed mb-6 font-medium text-left">
                {role === "admin" ? (
                  <span>🔒 <strong>Super Admin Launch Rule:</strong> Live studio launch unlocks strictly <strong>2 minutes before</strong> scheduled class time. If you wish to start right now, please edit/prepone the session in the Admin panel.</span>
                ) : (
                  <span>🧘 Class will go live at the scheduled time. Please check back when class starts.</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate(role === "admin" ? "/admin/livesessions" : "/user/livesession")}
                  className="w-full py-3 rounded-full text-xs font-black uppercase tracking-wider text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 shadow-lg cursor-pointer border-none transition-all active:scale-95"
                >
                  {role === "admin" ? "Back to Live Sessions Panel" : "Return to Live Sessions"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
